import {
    createAsyncThunk,
    createSlice,
    PayloadAction,
} from "@reduxjs/toolkit";
import * as transportOrderDetailsService from "../../../services/transportOrderDetailsService";
import {
    TransportOrderDetailsDto,
    TransportOrderUpdateDto,
} from "../../../models/transport/transportOrder";
import { RootState } from "../store";
import { createTransportOrder, deleteTransportOrder, editTransportOrderWithNotification, updateTransportOrder, updateTransportOrderEmployee } from "../../../services/transportOrderService";
import { TransportOrderSaveData } from "../../../models/transport/transportOrderSaveData";

const initialOrderState: TransportOrderDetailsDto = {
    id: "",
    name: "",
    vehicleId: null,
    employeeId: null,
    scheduledStartDateTime: null,
    scheduledEndDateTime: null,
    transportDetails: null,
    invoicingState: 0,
    senderName: "",
    senderBusinessId: null,
    senderCustomerId: null,
    receiverBusinessId: null,
    receiverCustomerId: null,
    driverComments: null,
    orderNumber: null,
    billingCustomerId: null,
    billingCustomerName: null,
    state: 0,
    invoiceNewId: null,
    createdEmployeeId: null,
    createdDateTime: null,
    createdEmployeeName: null,
    modifiedEmployeeId: null,
    modifiedDateTime: null,
    latestTransportTaskType: null,
    latestTransportTaskTypeChangedDateTime: null,
    vehicleName: null,
    employeeName: null,
    receiverName: null,
    orderTypeId: null,
    formValues: null,
    uiSchema: null,
    formData: null,
    formTitle: null,
    products: [],
    transportOrderNotifications: [],
    totalSum: null,
    totalSumWithVat: null,
};

const initialState = {
    order: initialOrderState,
    editingOrder: initialOrderState,
};

export const transportOrderDetailsSlice = createSlice({
    name: "transportOrderDetails",
    initialState,
    reducers: {
        setTransportOrderDetails: (state, action: PayloadAction<TransportOrderDetailsDto>) => {
            state.order = action.payload;
            state.editingOrder = action.payload;
        },
        updateEditingOrder: (state, action: PayloadAction<Partial<TransportOrderDetailsDto>>) => {
            state.editingOrder = { ...state.editingOrder, ...action.payload };
        },
        resetEditingOrder: (state) => {
            state.editingOrder = state.order;
        }
    }
});

export const getOrderDetails = createAsyncThunk(
    "transportOrderDetails/details",
    async({ orderId }: { orderId: string }, thunkApi) => {
        const res = await transportOrderDetailsService.getTransportOrderDetails({ orderId }, thunkApi.signal);
        thunkApi.dispatch(setTransportOrderDetails(res.data));
    }
);

export const saveTransportOrderWithNotification = createAsyncThunk(
    "transportOrderDetails/saveWithNotification",
    async(
        { updateDto, orderId }: { updateDto: TransportOrderUpdateDto; orderId: string },
        thunkApi
    ) => {
        try {
            const response = await editTransportOrderWithNotification(updateDto, orderId, thunkApi.signal);
            thunkApi.dispatch(setTransportOrderDetails(response.data));
            return response;
        } catch (error: any) {
            return thunkApi.rejectWithValue(error?.message);
        }
    }
);

export const saveTransportOrderEmployee = createAsyncThunk(
    "transportOrderDetails/saveEmployee",
    async(
        { orderId, employeeId }: { orderId: string; employeeId?: string },
        thunkApi
    ) => {
        try {
            const response = await updateTransportOrderEmployee(orderId, employeeId, thunkApi.signal);
            thunkApi.dispatch(setTransportOrderDetails(response.data));
            return response;
        } catch (error: any) {
            return thunkApi.rejectWithValue(error?.message);
        }
    }
);

export const addTransportOrder = createAsyncThunk(
    "transportOrder/add",
    async(data: TransportOrderSaveData, thunkApi) => {
        const response = await createTransportOrder(data);
        thunkApi.dispatch(setTransportOrderDetails(response.data));
    }
);

export const updateExistingTransportOrder = createAsyncThunk(
    "transportOrder/update",
    async(
        { orderId, data }: { orderId: string; data: TransportOrderSaveData },
        thunkApi
    ) => {
        const response = await updateTransportOrder(orderId, data, thunkApi.signal);
        thunkApi.dispatch(setTransportOrderDetails(response.data));
        return response;
    }
);

export const removeTransportOrder = createAsyncThunk(
    "transportOrder/remove",
    async(orderId: string) => {
        await deleteTransportOrder(orderId);
    }
);

export const { setTransportOrderDetails, updateEditingOrder, resetEditingOrder } = transportOrderDetailsSlice.actions;

export const transportOrderDetailsReducer = transportOrderDetailsSlice.reducer;

export const selectOrder = (state: RootState) : TransportOrderDetailsDto => state.transportOrderDetails.order;
